/* eslint-disable no-console */

import axios from 'axios';
import ErrorMessage from '../Elements/ErrorMessage';
import dateTimeHelper from '../Helpers/datetimeHelper';
import CookieHelper from '../Helpers/cookieHelper';
import adconfig from '../ADConfig.json';
import { getToken, logout as msalLogout } from '../Services/msalClient.ts';
import { msalInstance, msalReady, signIn } from '../Services/msalClient.ts';
import regexHelper from '../Helpers/regexHelper.js';
import SignInBlockedReasonEnum, { SIGN_IN_ERROR_MARKERS } from '../Enums/SignInBlockedReasonEnum.ts';

// Backend business failures (controller `Problem(...)`, ASP.NET ProblemDetails) carry the
// user-facing message in `detail`. Rethrow it flagged so callers can show the real message instead
// of their generic error code; infrastructure failures keep the generic path.
// TEMPORARY solution: the app-wide error-handling rework (proper status codes end to end,
// central handling instead of per-catch opt-in) is CD-5110 — revisit/replace this there.
const throwIfProblemDetail = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) {
    const businessError = new Error(detail);
    businessError.isBusinessError = true;
    throw businessError;
  }
};

class AuthService {
  static myInstance = null;

  // Shown on every 423 while the account is locked. The backend's matching texts (the in-app message and the user
  // email) quote the configured window; this one can't read it, so it doesn't name a number.
  static accountLockedMessage =
    "Your Bravo account has been temporarily locked because it was used from several locations or networks within a short period of time. It unlocks automatically, usually within a few minutes of your last activity from the other location. If this wasn't you, please contact IT support.";

  static getInstance() {
    if (AuthService.myInstance == null) {
      AuthService.myInstance = new AuthService();
      AuthService.myInstance
        .Login()
        .then(() => {
          console.log('Login completed');
        })
        .catch((error) => {
          console.error('Login failed: ', error);
          throw error;
        });
    }
    return AuthService.myInstance;
  }

  constructor() {
    this.state = {
      AuthenticatedApi: axios.create({
        baseURL: process.env.REACT_APP_BASE_URL ?? 'https://localhost:44340',
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      }),
      loggedInUsers: [],
      signInBlockedReason: null,
      switchUserCallbacks: [],
      userResolvedCallbacks: [],
    };

    // Attach token before each request
    this.state.AuthenticatedApi.interceptors.request.use(async (config) => {
      await msalReady;

      // If there is no account, start login and CANCEL this request.
      if (msalInstance.getAllAccounts().length === 0) {
        signIn();
        // Cancel the axios request so it doesn't hit your API unauthenticated
        return Promise.reject(new axios.Cancel('Redirecting to login'));
      }
      //getToken().then((t) => console.log(JSON.parse(atob(t.split('.')[ 1 ]))));
      const token = await getToken(); // from msalClient
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  ClearCache() {
    console.log('Clearing cache and reloading...');
    CookieHelper.setCookie('currentUser', '');
    signIn(); // funnel through the single guarded interactive sign-in
  }

  async Login() {
    await msalReady;
    // If no account yet, do nothing; the redirect will happen from the interceptor
    if (msalInstance.getAllAccounts().length === 0) return '';

    const currentComponent = this;
    try {
      const url = '/api/User?getLoggedInUser=true&getUserRolesFromClaim=true';

      return this.state.AuthenticatedApi.get(url)
        .then((response) => {
          console.log('Login response: ', response);
          // An EMPTY ARRAY specifically — not merely something of length 0. A 200 with an empty
          // body arrives as data: '', whose length is also 0, and reporting an infrastructure
          // blip to the user as a deactivated account would be both wrong and unactionable.
          // A non-array falls through to the retry path below, which is what it wants.
          if (Array.isArray(response.data) && response.data.length === 0) {
            // No active Bravo user for this Azure account. Stop here and let the app render
            // the sign-in-unavailable page (CD-5515) — the previous behaviour sent the user
            // back to the Microsoft account picker every 25s, with no way out and no
            // explanation, because signing in again produces the same empty response.
            console.error('No active Bravo user found for the signed-in Azure account.');
            CookieHelper.setCookie('currentUser', '');
            currentComponent.state.signInBlockedReason = SignInBlockedReasonEnum.NoActiveUser;
            currentComponent.NotifyUserResolved();
            return '';
          }
          // The token identified nobody, so the backend could not even look an account up. Kept
          // apart from "no active user": reactivating an account cannot fix it.
          if (response.data.error === SIGN_IN_ERROR_MARKERS.NoIdentityClaim) {
            console.error('The bearer token carried no identity claim; cannot resolve a Bravo user.');
            CookieHelper.setCookie('currentUser', '');
            currentComponent.state.signInBlockedReason = SignInBlockedReasonEnum.NoIdentityClaim;
            currentComponent.NotifyUserResolved();
            return '';
          }
          if (response.data.error === SIGN_IN_ERROR_MARKERS.MaintenanceInProgress) {
            ErrorMessage.SetErrorMessage('Maintenance is in progress... 1');
            return null;
          }
          // console.log('Login successful, user data retrieved: ', response.data);
          currentComponent.state.loggedInUsers = Array.isArray(response.data) ? response.data : [];
          // If the currentUser has a value, and the value is found in the loggedInUsers array,
          // set the inUse property to true for that user item.
          // This means that the switch user functionality will work as expected, even when opening new Bravo tabs.
          const currentUser = CookieHelper.getCookie('currentUser');
          const users = currentComponent.state.loggedInUsers;
          const matchedUser = currentUser ? users.find((u) => u.userId?.toString() === currentUser) : null;
          if (matchedUser) {
            matchedUser.inUse = true;
          } else if (users.length > 0) {
            users[0].inUse = true;
            CookieHelper.setCookie('currentUser', users[0].userId);
          }

          currentComponent.NotifyUserResolved();
          return '';
        })
        .catch((error) => {
          console.error('Login failed:', error);
        });
    } catch (err) {
      console.error('Login failed:', err);
      if (axios.isCancel?.(err)) {
        // We intentionally cancelled during login redirect; nothing to do.
        return '';
      }
      throw err;
    }
  }

  Logout() {
    CookieHelper.setCookie('currentUser', '');
    msalLogout(); // from msalClient.ts
  }

  // Pure read: returns the currently in-use user (or undefined) without any
  // side effects. Login is kicked off once by getInstance(); consumers that
  // need to react to login completion subscribe via listenUserResolved().
  GetLoggedInUser() {
    const users = Array.isArray(this.state.loggedInUsers) ? this.state.loggedInUsers : [];
    return users.find((u) => u.inUse);
  }

  // Non-null once Login() has established that this Azure account cannot use Bravo.
  // Terminal: only a different account (or reactivation) changes it, so callers should
  // stop waiting for a user and show the reason rather than retrying.
  GetSignInBlockedReason() {
    return this.state.signInBlockedReason;
  }

  GetTimesheetUser() {
    const users = Array.isArray(this.state.loggedInUsers) ? this.state.loggedInUsers : [];
    return users.find((u) => u.timesheetUser !== null);
  }

  static IsAdmin() {
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const roles = loggedInUser?.roles;
    if (roles === undefined) return false;
    return roles.includes('Admin') || roles.includes('Developer');
  }

  static HasRole(role) {
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const roles = loggedInUser?.roles;
    if (roles === undefined) return false;
    return (
      roles.includes('Admin') || roles.includes('Developer') || roles.some((x) => regexHelper.matchWildcard(x, role))
    );
  }

  static HasRoleArray(roleArray) {
    const roles = AuthService.getInstance().GetLoggedInUser()?.roles;
    if (roles === undefined) return false;
    if (roles.includes('Admin') || roles.includes('Developer')) return true;
    for (const role of roleArray) {
      if (roles.some((x) => regexHelper.matchWildcard(x, role))) return true;
    }
    return false;
  }

  GetLoggedInUsersNotInUse() {
    const users = Array.isArray(this.state.loggedInUsers) ? this.state.loggedInUsers : [];
    const notInUse = users.filter((u) => !u.inUse);
    return notInUse.length > 0 ? notInUse : [];
  }

  switchToUser(toUser) {
    const users = Array.isArray(this.state.loggedInUsers) ? this.state.loggedInUsers : [];
    const currentInUse = users.find((user) => user.inUse);
    if (currentInUse) currentInUse.inUse = false;
    const targetUser = users.find((user) => user.userName === toUser.userName);
    if (targetUser) targetUser.inUse = true;
    CookieHelper.setCookie('currentUser', toUser.userId);
    window.location.reload();
  }

  listenSwitchUserEvent(callback) {
    this.state.switchUserCallbacks.push(callback);
  }

  unlistenSwitchUserEvent(callback) {
    this.state.switchUserCallbacks = this.state.switchUserCallbacks.filter((c) => c !== callback);
  }

  // Subscribe to be notified once Login() has resolved the logged-in user(s),
  // so consumers can react to login completion instead of polling for it.
  listenUserResolved(callback) {
    this.state.userResolvedCallbacks.push(callback);
  }

  unlistenUserResolved(callback) {
    this.state.userResolvedCallbacks = this.state.userResolvedCallbacks.filter((c) => c !== callback);
  }

  NotifyUserResolved() {
    this.state.userResolvedCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('User-resolved callback failed:', error);
      }
    });
  }

  GetApiUrl() {
    return process.env.REACT_APP_BASE_URL;
  }

  GetEnvironment() {
    return process.env.REACT_APP_HOST_ENV;
  }

  CallBackendFunction(functionAddress) {
    const date = dateTimeHelper.getFormattedDateTime(new Date());
    console.log('Calling web api (get): ' + functionAddress + ' at ' + date);
    ErrorMessage.LogDevInfo(functionAddress, 'authservice.CallBackendFunction row 213', ErrorMessage.NotProd);
    if (!functionAddress.includes('clearCache') && !functionAddress.includes('searchParams')) {
      ErrorMessage.LogDevInfo(
        `Developer info: Missing clearCache or searchParams at: ${functionAddress}`,
        'authservice.CallBackendFunction row 216'
      );
      functionAddress += `${functionAddress.includes('?') ? '&' : '?'}clearCache=true`;
      ErrorMessage.LogDevInfo(`Modified url: ${functionAddress}`, 'authservice.CallBackendFunction row 218');
    }

    const tooLongSearch = this.RejectTooLongSearchUrl(functionAddress);
    if (tooLongSearch) {
      return tooLongSearch;
    }

    return this.state.AuthenticatedApi.get(functionAddress)
      .then((response) => {
        ErrorMessage.LogDevInfo(
          ` Web api response to ${functionAddress}: ${response}`,
          'authservice.CallBackendFunction row 224',
          ErrorMessage.NotDev
        );
        ErrorMessage.LogDevInfo(response, 'authservice.CallBackendFunction row 225', ErrorMessage.NotDev);
        return response.data;
      })
      .catch((error) => {
        ErrorMessage.LogDevInfo(
          `Error from ${functionAddress}`,
          'authservice.CallBackendFunction row 230',
          ErrorMessage.NotDev
        );
        ErrorMessage.LogDevInfo(error, 'authservice.CallBackendFunction row 231', ErrorMessage.NotDev);
        ErrorMessage.LogDevInfo(error.response, 'authservice.CallBackendFunction row 232', ErrorMessage.NotDev);
        error.response &&
          ErrorMessage.LogDevInfo(error.response.data, 'authservice.CallBackendFunction row 233', ErrorMessage.NotDev);
        error.response &&
          ErrorMessage.LogDevInfo(
            error.response.status,
            'authservice.CallBackendFunction row 234',
            ErrorMessage.NotDev
          );
        error.response &&
          ErrorMessage.LogDevInfo(
            error.response.headers,
            'authservice.CallBackendFunction row 235',
            ErrorMessage.NotDev
          );

        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        throwIfProblemDetail(error);
        if (error !== undefined && error.response !== undefined && error.response.status === 401) {
          window.location.reload(true);
        } else {
          throw Error(error);
        }
      })
      .then((data) => {
        // GlobalExceptionHandlerMiddleware returns unhandled server exceptions as 200 { error: "...Guid: <guid>" }.
        // Components that expect list/object data (e.g. dropdown selects) otherwise process that shape as data, crash,
        // and swallow the message behind a generic code — hiding the log Guid needed to trace the error. Surface it and
        // reject so the caller fails into its .catch. This runs AFTER the axios .catch above (which dereferences
        // error.response) so a plain rejection here does not re-enter that axios-only handler. Scoped to guid-bearing
        // envelopes so the many deliberate, handled `Ok(new { Error = ... })` responses keep their existing behaviour.
        if (
          data &&
          typeof data === 'object' &&
          !Array.isArray(data) &&
          typeof data.error === 'string' &&
          data.error.includes('Guid:')
        ) {
          ErrorMessage.SetErrorMessage(data.error);
          return Promise.reject(new Error(data.error));
        }
        return data;
      });
  }

  CallBackendFunctionGetFile(functionAddress) {
    return this.state.AuthenticatedApi.get(functionAddress, { responseType: 'blob' })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        if (error !== undefined && error.response !== undefined && error.response.status === 401) {
          window.history.pushState({}, '', '/');
          window.location.reload(true);
        } else {
          // throw Error('An error has occurred calling the api (get): ' + error);
          throw Error(error);
        }
      });
  }

  // A search travels in the query string (`searchParams=` or, for the older export/bulk endpoints, `searchItem=`), and
  // IIS answers a URL over ~2000 characters with a bare 404.15 - so refuse it here with a message the user can act on.
  // A pasted id list (InputTypesEnum.TextList) is the usual way to get there. Returns the rejected promise, or null.
  RejectTooLongSearchUrl(functionAddress) {
    if (!functionAddress.includes('searchParams') && !functionAddress.includes('searchItem')) {
      return null;
    }
    //The default encoding somehow ignores these characters so we also revert them to their unescaped forms
    const encodedString = encodeURI(this.GetApiUrl() + functionAddress)
      .replaceAll('%7B', '{')
      .replaceAll('%7D', '}')
      .replaceAll('%5B', '[')
      .replaceAll('%5D', ']');
    if (encodedString.length <= 2000) {
      return null;
    }
    const message = 'The search parameters exceed the possible length. Please shorten the text search parameters.';
    ErrorMessage.SetErrorMessage(message);
    return Promise.reject(new Error(message));
  }

  CallBackendFunctionPost(functionAddress, data) {
    const tooLongSearch = this.RejectTooLongSearchUrl(functionAddress);
    if (tooLongSearch) {
      return tooLongSearch;
    }

    return this.state.AuthenticatedApi.post(functionAddress, data)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        throwIfProblemDetail(error);
        console.log(error);
        throw Error(
          `An error has occurred calling the api: ${error}, ${functionAddress /*+", "+JSON.stringify(data)*/}`
        );
      });
  }

  CallBackendFunctionPostFile(functionAddress, data) {
    //File size limit handling
    console.log(functionAddress);
    console.log(data);
    try {
      if (data?.has('file')) {
        const file = data.get('file');
        const fileSize = file?.size ? file.size : 0;
        if (fileSize / 1024 / 1024 > 25.0) {
          ErrorMessage.SetErrorMessage('The file exceeds the maximum allowed size of 25 MB.');
          return Promise.reject(new Error('The file exceeds the maximum allowed size of 25 MB.'));
        }
      }
    } catch (e) {
      console.log('Error checking file size: ', e);
    }

    const config = {
      headers: {
        'content-type': 'multipart/form-data;',
      },
    };

    return this.state.AuthenticatedApi.post(functionAddress, data, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        throwIfProblemDetail(error);
        console.log(error);
        throw Error(
          `An error has occurred calling the api: ${error}, ${functionAddress /*+", "+JSON.stringify(data)*/}`
        );
      });
  }

  CallBackendFunctionPut(functionAddress, data) {
    const date = dateTimeHelper.getFormattedDateTime(new Date());
    console.log(`${date} Calling web api (put): ${functionAddress}`);
    //console.log(data);

    return this.state.AuthenticatedApi.put(functionAddress, data)
      .then((response) => {
        const date = dateTimeHelper.getFormattedDateTime(new Date());
        console.log(`${date} Web api response to ${functionAddress}:`);
        //console.log(response);
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        throwIfProblemDetail(error);
        //authContext.login();
        console.log(error);
        // throw Error('An error has occurred calling the api: ' + error);
        throw Error(error);
      });
  }

  CallBackendFunctionDelete(functionAddress) {
    const date = dateTimeHelper.getFormattedDateTime(new Date());
    console.log(date + ' Calling web api (delete): ' + functionAddress);

    return this.state.AuthenticatedApi.delete(functionAddress)
      .then((response) => {
        // var date = datetimeHelper.getFormattedDateTime(new Date());
        // console.log(date + " Web api response: "+response+" to "+functionAddress);
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 429) {
          ErrorMessage.SetErrorMessage('Too many requests. Please try again in a minute.', error);
          throw Error(error);
        }
        if (error.response.status === 423) {
          ErrorMessage.SetErrorMessage(AuthService.accountLockedMessage, error);
          throw Error(error);
        }
        throwIfProblemDetail(error);
        console.log(error);
        throw Error(error);
      });
  }
}
export default AuthService;
